/*
  Veylum: keeps the cookie consent button off every control of the page, the price and the product photos.
  The consent app (Pandectes, <pandectes-cmp>) shows a round "Change your consent" button fixed in a corner of the
  screen. On a phone that corner falls on the full-width "Add to cart" button (with the size list and its "Size guide"
  link just above it), the sticky buy bar or the cart "Check out" button. While the page scrolls it also falls on
  links, buttons, fields and accordions: a footer link, a product card, a menu link, a question of the FAQ. It can also
  fall on the product title, the price, its tax line or the stock line of the product page, on the text of an open
  answer (FAQ), or on a product photo.
  Only while it would cover any visible control or one of those lines, the button moves: at the same height on the
  other side when that side is free, or just above them (above the whole cart summary when there is room), on its own
  side or on the other one, preferring a spot that covers no control, no product photo and none of those lines; when
  none of these spots is free, the nearest free height is searched on both sides, a few pixels at a time, and when no
  height is free, it takes the spot that hides the smallest part of a control. It goes back to its corner as soon as
  the way is clear. The title, price, tax and stock lines are read line by line, so a short
  price on the left leaves the right side free. It stays visible and usable, so the consent preferences remain one tap
  away. Nothing of the app is renamed, removed or restyled: one style rule added inside its shadow root offsets the
  button, and only that button.
*/
(function () {
  // On the product page, the size list and its "Size guide" link, just above the buy button, belong to the purchase
  // group: the button climbs above them instead of resting on the list.
  var PURCHASE = '.product-form__buttons, .veylum-sticky-buy:not([hidden]), .cart__ctas, .product__info-container variant-selects, .product__info-container .veylum-size-guide';
  // Product name, price, tax line and stock line of the product page, and the text of an open answer (FAQ page and
  // product questions): read as lines of text.
  var KEEP = '.product__info-container .product__title, .product__info-container .price, .product__info-container .veylum-price-note, .product__info-container .product__inventory, details[open] > .accordion__content';
  // Links and buttons of the menu, counted only while the menu is open (a closed menu is hidden off screen).
  var MENU = '.menu-drawer a[href], .menu-drawer button, .menu-drawer summary';
  var MEDIA = '.product__media-list img, .product__media img';
  var SUMMARY = '.drawer__footer, .cart__footer';
  var CONTROLS = 'a[href], button, input:not([type="hidden"]), select, textarea, summary, label, [role="button"]';
  var GAP = 8;
  var STEP = 4;
  var pending = false;
  var watched = null;

  function inView(r, width, height) {
    return r.width > 1 && r.height > 1 && r.bottom > 0 && r.top < height && r.right > 0 && r.left < width;
  }

  // Boxes of the elements on screen. An element of one pixel or less (a field hidden for screen readers only) does not
  // count.
  function rects(selector, width, height, shown) {
    var out = [];
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      var r = nodes[i].getBoundingClientRect();
      if (!inView(r, width, height)) continue;
      if (shown && getComputedStyle(nodes[i]).visibility === 'hidden') continue;
      out.push(r);
    }
    return out;
  }

  // The boxes of the lines of text of each element (one per line of each text), not the boxes of its blocks. A text whose
  // element has no real size (a label hidden for screen readers only) does not count.
  function lines(selector, width, height) {
    var out = [];
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      var walker = document.createTreeWalker(nodes[i], NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        var text = walker.currentNode;
        if (!/\S/.test(text.nodeValue)) continue;
        var parent = text.parentElement && text.parentElement.getBoundingClientRect();
        if (!parent || !(parent.width > 1 && parent.height > 1)) continue;
        var range = document.createRange();
        range.selectNodeContents(text);
        var list = range.getClientRects();
        for (var j = 0; j < list.length; j++) if (inView(list[j], width, height)) out.push(list[j]);
      }
    }
    return out;
  }

  function covers(box, list) {
    for (var i = 0; i < list.length; i++) {
      var r = list[i];
      if (box.left < r.right + GAP && box.right > r.left - GAP && box.top < r.bottom + GAP && box.bottom > r.top - GAP) return true;
    }
    return false;
  }

  function box(x, y, w, h) {
    return {left: x, top: y, right: x + w, bottom: y + h};
  }

  // Highest point the button must climb to, from y, so that it covers none of the listed boxes (they can be stacked).
  function above(x, y, w, h, list) {
    for (var n = 0; n < list.length + 1; n++) {
      var hit = null;
      for (var i = 0; i < list.length; i++) {
        if (covers(box(x, y, w, h), [list[i]]) && (hit === null || list[i].top < hit)) hit = list[i].top;
      }
      if (hit === null) return y;
      y = hit - GAP - h;
    }
    return y;
  }

  function offset(root, dx, dy) {
    var style = root.querySelector('style[data-veylum-consent-button]');
    if (!style) {
      style = document.createElement('style');
      style.setAttribute('data-veylum-consent-button', '');
      root.appendChild(style);
    }
    var css = dx || dy
      ? '.widget{--translate-x:' + dx + 'px;--translate-y:' + dy + 'px}'
      : '';
    css += '@media (prefers-reduced-motion:no-preference){.widget{transition:transform .2s ease}}';
    if (style.textContent !== css) style.textContent = css;
  }

  function update() {
    pending = false;
    var host = document.querySelector('pandectes-cmp');
    var root = host && host.shadowRoot;
    if (!root) return;
    if (watched !== root) {
      watched = root;
      new MutationObserver(schedule).observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden']});
    }
    var button = root.querySelector('button.widget');
    if (!button) return;
    var list = button.classList;
    if (!(list.contains('left') || list.contains('right')) || list.contains('center') || list.contains('middle')) return;
    var cs = getComputedStyle(button);
    var x0 = parseFloat(cs.left);
    var y0 = parseFloat(cs.top);
    var w = button.offsetWidth;
    var h = button.offsetHeight;
    if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden' || !w || isNaN(x0) || isNaN(y0)) return;
    var width = document.documentElement.clientWidth;
    var height = window.innerHeight;
    var keep = lines(KEEP, width, height).concat(rects(MENU, width, height, true));
    var guard = rects(PURCHASE, width, height).concat(keep);
    // Every visible control counts, not only the purchase ones. Hidden controls (the links of a closed menu, partly on
    // screen) do not.
    var controls = rects(CONTROLS, width, height, true);
    var media = rects(MEDIA, width, height);
    if (!covers(box(x0, y0, w, h), guard.concat(controls, media))) return offset(root, 0, 0);

    var header = document.querySelector('.section-header');
    var top = GAP + (header ? Math.max(0, header.getBoundingClientRect().bottom) : 0);
    var spots = [];
    var sets = [guard.concat(rects(SUMMARY, width, height)), guard, guard.concat(controls)];
    var xs = [x0, width - x0 - w];
    for (var s = 0; s < sets.length; s++) {
      for (var i = 0; i < xs.length; i++) {
        var y = above(xs[i], y0, w, h, sets[s]);
        if (y >= top) spots.push([xs[i], y]);
      }
    }
    // First a spot that covers nothing to keep clear, then one that covers no control and no kept line, then one that
    // covers no control. For each, the spots above first, then the nearest free height on either side.
    var avoid = [controls.concat(keep, media), controls.concat(keep), controls];
    var heights = [];
    for (var z = y0; z >= top; z -= STEP) heights.push(z);
    for (var a = 0; a < avoid.length; a++) {
      for (var j = 0; j < spots.length; j++) {
        if (!covers(box(spots[j][0], spots[j][1], w, h), avoid[a])) return offset(root, Math.round(spots[j][0] - x0), Math.round(spots[j][1] - y0));
      }
      for (var n = 0; n < heights.length; n++) {
        for (var k = 0; k < xs.length; k++) {
          if (!covers(box(xs[k], heights[n], w, h), avoid[a])) return offset(root, Math.round(xs[k] - x0), Math.round(heights[n] - y0));
        }
      }
    }
    // No free spot at all (a stack of full-width fields or rows): the spot that hides the smallest part of any control or
    // kept line (the empty end of a wide field rather than a short link), the corner or the nearest spot first.
    var shared = controls.concat(keep);
    var best = null;
    var tries = [[x0, y0]].concat(spots);
    for (var t = 0; t < heights.length; t++) tries.push([xs[0], heights[t]], [xs[1], heights[t]]);
    for (var q = 0; q < tries.length; q++) {
      var worst = 0;
      for (var m = 0; m < shared.length; m++) worst = Math.max(worst, hidden(box(tries[q][0], tries[q][1], w, h), shared[m]));
      if (best === null || worst < best[2] - 0.01) best = [tries[q][0], tries[q][1], worst];
    }
    offset(root, Math.round(best[0] - x0), Math.round(best[1] - y0));
  }

  // Part of a box (0 to 1) hidden by the button.
  function hidden(b, r) {
    var area = (r.right - r.left) * (r.bottom - r.top);
    if (!(area > 0)) return 0;
    var x = Math.min(b.right, r.right) - Math.max(b.left, r.left);
    var y = Math.min(b.bottom, r.bottom) - Math.max(b.top, r.top);
    return x > 0 && y > 0 ? (x * y) / area : 0;
  }

  function schedule() {
    if (!pending) {
      pending = true;
      requestAnimationFrame(update);
    }
  }

  window.addEventListener('scroll', schedule, {passive: true});
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  document.addEventListener('transitionend', schedule, true);
  new MutationObserver(schedule).observe(document.body, {childList: true, attributes: true, attributeFilter: ['class']});
  if (window.ResizeObserver) new ResizeObserver(schedule).observe(document.body);
  schedule();
})();
